import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { DEFAULT_WATERMARK_SETTINGS } from "../features/tools/watermark/Popup/watermarkSettings";

export const DEFAULT_OBJECT_STATE = {
  rotation: 0,
  flipH: false,
  flipV: false,

  shadow: {
    enabled: false,
    x: 0,
    y: 0,
    blur: 0,
    opacity: 1,
    color: "#000000",
  },

  outline: {
    enabled: false,
    width: 0,
    blur: 0,
    color: "#000000",
  },

  watermark: {
    enabled: false,
    ...DEFAULT_WATERMARK_SETTINGS,
  },
};

export const DEFAULT_BACKGROUND = {
  type: null,
  color: "transparent",
  imageUrl: null,
  aiConfig: null,
  /** Unsplash credit when type === "image" from stock catalog/search */
  attribution: null,
};

/** Core layers always present. Image overlays are appended dynamically. */
export const DEFAULT_LAYERS = [
  {
    id: "background",
    type: "background",
    label: "Background",
    visible: true,
    locked: true,
    zIndex: 0,
  },
  {
    id: "object",
    type: "object",
    label: "Object",
    visible: true,
    locked: false,
    zIndex: 1,
  },
];

export function createDefaultLayers() {
  return DEFAULT_LAYERS.map((layer) => ({ ...layer }));
}

export function createImageLayer({
  id,
  imageUrl,
  label,
  zIndex,
  x,
  y,
  width,
  height,
  rootCreationId = null,
}) {
  return {
    id,
    type: "image",
    label: label || "Image",
    visible: true,
    locked: false,
    zIndex: Number(zIndex) || 1,
    imageUrl: imageUrl || null,
    /** Original URL before BG removal (for toggle off). */
    sourceImageUrl: null,
    /** Cached cutout URL after BG removal. */
    cutoutImageUrl: null,
    /** Uploads-library root creation id — fetch via API to avoid S3 CORS. */
    rootCreationId:
      rootCreationId != null && Number.isFinite(Number(rootCreationId))
        ? Number(rootCreationId)
        : null,
    /** Opaque subject crop in natural px (CanvasEditor-style bbox after BG remove). */
    contentCrop: null,
    /** Full-frame layout before contentCrop tighten (for toggle off). */
    sourceLayout: null,
    bgRemoved: false,
    x: Number(x) || 0,
    y: Number(y) || 0,
    width: Math.max(1, Number(width) || 100),
    height: Math.max(1, Number(height) || 100),
    rotation: 0,
    flipH: false,
    flipV: false,
  };
}

function normalizeCoreLayer(def, found) {
  if (!found) return { ...def };
  const label =
    typeof found.label === "string" && found.label.trim()
      ? found.label.trim()
      : def.label;
  return {
    ...def,
    label,
    visible: found.visible !== false,
    zIndex:
      def.id === "background"
        ? 0
        : Number.isFinite(Number(found.zIndex))
          ? Number(found.zIndex)
          : def.zIndex,
  };
}

function normalizeImageLayer(raw, fallbackZ) {
  if (!raw || typeof raw !== "object" || !raw.id) return null;
  const imageUrl = typeof raw.imageUrl === "string" ? raw.imageUrl : null;
  if (!imageUrl) return null;
  return {
    ...createImageLayer({
      id: String(raw.id),
      imageUrl,
      label: typeof raw.label === "string" ? raw.label : "Image",
      zIndex: Number.isFinite(Number(raw.zIndex)) ? Number(raw.zIndex) : fallbackZ,
      x: Number(raw.x) || 0,
      y: Number(raw.y) || 0,
      width: Math.max(1, Number(raw.width) || 100),
      height: Math.max(1, Number(raw.height) || 100),
    }),
    visible: raw.visible !== false,
    sourceImageUrl:
      typeof raw.sourceImageUrl === "string" ? raw.sourceImageUrl : null,
    cutoutImageUrl:
      typeof raw.cutoutImageUrl === "string" ? raw.cutoutImageUrl : null,
    rootCreationId:
      raw.rootCreationId != null && Number.isFinite(Number(raw.rootCreationId))
        ? Number(raw.rootCreationId)
        : null,
    contentCrop:
      raw.contentCrop &&
      typeof raw.contentCrop === "object" &&
      Number(raw.contentCrop.width) > 0 &&
      Number(raw.contentCrop.height) > 0
        ? {
            x: Number(raw.contentCrop.x) || 0,
            y: Number(raw.contentCrop.y) || 0,
            width: Number(raw.contentCrop.width),
            height: Number(raw.contentCrop.height),
            imageWidth: Number(raw.contentCrop.imageWidth) || 0,
            imageHeight: Number(raw.contentCrop.imageHeight) || 0,
          }
        : null,
    sourceLayout:
      raw.sourceLayout &&
      typeof raw.sourceLayout === "object" &&
      Number(raw.sourceLayout.width) > 0 &&
      Number(raw.sourceLayout.height) > 0
        ? {
            x: Number(raw.sourceLayout.x) || 0,
            y: Number(raw.sourceLayout.y) || 0,
            width: Number(raw.sourceLayout.width),
            height: Number(raw.sourceLayout.height),
          }
        : null,
    bgRemoved: Boolean(raw.bgRemoved),
    rotation: Number(raw.rotation) || 0,
    flipH: Boolean(raw.flipH),
    flipV: Boolean(raw.flipV),
  };
}

/** Ensure background + object exist; keep image overlays; re-clamp z-order. */
export function normalizeLayers(layers) {
  const list = Array.isArray(layers) ? layers : [];
  const background = normalizeCoreLayer(
    DEFAULT_LAYERS[0],
    list.find((l) => l && l.id === "background")
  );
  const object = normalizeCoreLayer(
    DEFAULT_LAYERS[1],
    list.find((l) => l && l.id === "object")
  );
  const images = list
    .filter((l) => l && l.type === "image" && l.id !== "background" && l.id !== "object")
    .map((l, i) => normalizeImageLayer(l, 2 + i))
    .filter(Boolean);

  // Preserve relative order among object + images by zIndex, background fixed at 0.
  const movable = [object, ...images].sort(
    (a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)
  );
  const reindexed = movable.map((layer, i) => ({
    ...layer,
    zIndex: i + 1,
  }));
  return [background, ...reindexed];
}

export function isLayerVisible(layers, layerId) {
  const layer = (layers || []).find((l) => l.id === layerId);
  return layer ? layer.visible !== false : true;
}

export function getImageLayers(layers) {
  return normalizeLayers(layers).filter((l) => l.type === "image");
}

export function sortLayersTopFirst(layers) {
  return [...normalizeLayers(layers)].sort(
    (a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0)
  );
}

function nextImageLayerId() {
  return `img_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function collectLayerBlobUrls(layers) {
  const urls = [];
  for (const layer of layers || []) {
    if (layer?.type !== "image") continue;
    for (const key of ["imageUrl", "sourceImageUrl", "cutoutImageUrl"]) {
      if (typeof layer[key] === "string") urls.push(layer[key]);
    }
  }
  return urls;
}

const HISTORY_CAP = 30;
const SESSION_STORAGE_KEY = "editor-session";
const MAX_PERSIST_URL_LEN = 2048;

const revokeIfBlob = (url) => {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

/** Layer fields that may hold a live blob URL (not only the visible imageUrl). */
function layerHoldsBlobUrl(layer, url) {
  if (!layer || layer.type !== "image") return false;
  return (
    layer.imageUrl === url ||
    layer.sourceImageUrl === url ||
    layer.cutoutImageUrl === url
  );
}

/** True if blob URL is still needed by present state or undo stacks. */
const isBlobUrlReferenced = (url, state) => {
  if (typeof url !== "string" || !url.startsWith("blob:")) {
    return false;
  }
  if (state.imageUrl === url) {
    return true;
  }
  // Main-subject cutout kept while BG toggle is off (must not revoke).
  if (state.subjectCutoutUrl === url) {
    return true;
  }
  for (const layer of state.layers || []) {
    if (layerHoldsBlobUrl(layer, url)) return true;
  }
  for (const asset of state.uploadAssets || []) {
    if (asset?.imageUrl === url) return true;
  }
  for (const snap of state.past) {
    if (snap.imageUrl === url) return true;
    if (snap.subjectCutoutUrl === url) return true;
    for (const layer of snap.layers || []) {
      if (layerHoldsBlobUrl(layer, url)) return true;
    }
  }
  for (const snap of state.future) {
    if (snap.imageUrl === url) return true;
    if (snap.subjectCutoutUrl === url) return true;
    for (const layer of snap.layers || []) {
      if (layerHoldsBlobUrl(layer, url)) return true;
    }
  }
  return false;
};

const revokeBlobIfUnreferenced = (url, state) => {
  if (!url || url === state.imageUrl) {
    return;
  }
  if (!isBlobUrlReferenced(url, state)) {
    revokeIfBlob(url);
  }
};

const maybeRevokeDroppedSnapshot = (dropped, stateAfter) => {
  const url = dropped?.imageUrl;
  if (!url?.startsWith("blob:")) return;
  if (!isBlobUrlReferenced(url, stateAfter)) {
    revokeIfBlob(url);
  }
};

const revokeAllSessionBlobs = (state) => {
  const seen = new Set();
  const consider = (url) => {
    if (typeof url === "string" && url.startsWith("blob:") && !seen.has(url)) {
      seen.add(url);
      revokeIfBlob(url);
    }
  };
  consider(state.imageUrl);
  consider(state.subjectCutoutUrl);
  for (const url of collectLayerBlobUrls(state.layers)) consider(url);
  for (const asset of state.uploadAssets || []) consider(asset?.imageUrl);
  for (const snap of state.past) {
    consider(snap.imageUrl);
    consider(snap.subjectCutoutUrl);
    for (const url of collectLayerBlobUrls(snap.layers)) consider(url);
  }
  for (const snap of state.future) {
    consider(snap.imageUrl);
    consider(snap.subjectCutoutUrl);
    for (const url of collectLayerBlobUrls(snap.layers)) consider(url);
  }
};

/** Deep-cloned undoable slice — never push live state references into past. */
export function captureSnapshot(state) {
  return JSON.parse(
    JSON.stringify({
      imageUrl: state.imageUrl,
      subjectCutoutUrl: state.subjectCutoutUrl ?? null,
      creationId: state.creationId,
      transparentSourceId: state.transparentSourceId,
      rootCreationId: state.rootCreationId,
      background: state.background,
      object: state.object,
      layers: normalizeLayers(state.layers),
    })
  );
}

const appendPast = (state) => {
  const past = [...state.past, captureSnapshot(state)];
  if (past.length > HISTORY_CAP) {
    const dropped = past.shift();
    maybeRevokeDroppedSnapshot(dropped, { ...state, past, future: [] });
  }
  return { past, future: [] };
};

const applySnapshotFields = (state, snapshot, previousImageUrl = state.imageUrl) => {
  const nextImageUrl = snapshot.imageUrl ?? null;
  const nextCutout =
    snapshot.subjectCutoutUrl !== undefined
      ? snapshot.subjectCutoutUrl
      : state.subjectCutoutUrl;
  const tentative = {
    ...state,
    imageUrl: nextImageUrl,
    subjectCutoutUrl: nextCutout ?? null,
  };
  if (previousImageUrl && previousImageUrl !== nextImageUrl) {
    revokeBlobIfUnreferenced(previousImageUrl, tentative);
  }
  return {
    imageUrl: nextImageUrl,
    subjectCutoutUrl: nextCutout ?? null,
    creationId:
      snapshot.creationId != null ? Number(snapshot.creationId) : null,
    transparentSourceId:
      snapshot.transparentSourceId != null
        ? Number(snapshot.transparentSourceId)
        : null,
    rootCreationId:
      snapshot.rootCreationId != null ? Number(snapshot.rootCreationId) : null,
    background: JSON.parse(JSON.stringify(snapshot.background)),
    object: JSON.parse(JSON.stringify(snapshot.object)),
    layers: normalizeLayers(snapshot.layers),
  };
};

/** Keep persisted session JSON small — never store blob/data URLs or huge payloads. */
function sanitizeBackgroundForPersist(background) {
  if (!background || typeof background !== "object") {
    return { ...DEFAULT_BACKGROUND };
  }
  const out = {
    type: background.type ?? null,
    color: typeof background.color === "string" ? background.color : "transparent",
    imageUrl: null,
    aiConfig: null,
    attribution: null,
  };
  const url = background.imageUrl;
  if (
    typeof url === "string" &&
    url.length > 0 &&
    url.length <= MAX_PERSIST_URL_LEN &&
    (url.startsWith("http://") || url.startsWith("https://"))
  ) {
    out.imageUrl = url;
  }
  if (background.aiConfig && typeof background.aiConfig === "object") {
    const prompt =
      typeof background.aiConfig.prompt === "string"
        ? background.aiConfig.prompt.slice(0, 500)
        : null;
    if (prompt) out.aiConfig = { prompt };
  }
  const attr = background.attribution;
  if (attr && typeof attr === "object" && (attr.photographer || attr.photo_url)) {
    out.attribution = {
      photographer:
        typeof attr.photographer === "string" ? attr.photographer.slice(0, 120) : null,
      photographer_url:
        typeof attr.photographer_url === "string" &&
        attr.photographer_url.startsWith("https://")
          ? attr.photographer_url.slice(0, 500)
          : null,
      photo_url:
        typeof attr.photo_url === "string" && attr.photo_url.startsWith("https://")
          ? attr.photo_url.slice(0, 500)
          : null,
    };
  }
  return out;
}

function sanitizeWatermarkForPersist(watermark) {
  const base = {
    ...DEFAULT_OBJECT_STATE.watermark,
  };
  if (!watermark || typeof watermark !== "object") {
    return { ...base };
  }
  const next = {
    ...base,
    ...watermark,
    enabled: Boolean(watermark.enabled),
    presetId:
      typeof watermark.presetId === "string" ? watermark.presetId : null,
  };
  // Blob logo URLs / File objects do not survive reload — drop them.
  next.logoFile = null;
  if (
    typeof next.logoUrl === "string" &&
    (next.logoUrl.startsWith("blob:") || next.logoUrl.startsWith("data:"))
  ) {
    next.logoUrl = null;
    next.logoNaturalWidth = 0;
    next.logoNaturalHeight = 0;
  }
  if (
    typeof next.logoOriginalUrl === "string" &&
    (next.logoOriginalUrl.startsWith("blob:") ||
      next.logoOriginalUrl.startsWith("data:"))
  ) {
    next.logoOriginalUrl = null;
  }
  return next;
}

function sanitizeObjectForPersist(object) {
  if (!object || typeof object !== "object") {
    return {
      ...DEFAULT_OBJECT_STATE,
      shadow: { ...DEFAULT_OBJECT_STATE.shadow },
      outline: { ...DEFAULT_OBJECT_STATE.outline },
      watermark: { ...DEFAULT_OBJECT_STATE.watermark },
    };
  }
  return {
    rotation: Number(object.rotation) || 0,
    flipH: Boolean(object.flipH),
    flipV: Boolean(object.flipV),
    shadow: { ...DEFAULT_OBJECT_STATE.shadow, ...(object.shadow || {}) },
    outline: { ...DEFAULT_OBJECT_STATE.outline, ...(object.outline || {}) },
    watermark: sanitizeWatermarkForPersist(object.watermark),
  };
}

function sanitizeLayersForPersist(layers) {
  return normalizeLayers(layers)
    .map((layer) => {
      if (layer.type !== "image") {
        return {
          id: layer.id,
          type: layer.type,
          label: layer.label,
          visible: layer.visible !== false,
          locked: Boolean(layer.locked),
          zIndex: Number(layer.zIndex) || 0,
        };
      }
      const url = layer.imageUrl;
      const persistable =
        typeof url === "string" &&
        url.length > 0 &&
        url.length <= MAX_PERSIST_URL_LEN &&
        (url.startsWith("http://") || url.startsWith("https://"));
      if (!persistable) return null;
      return {
        id: layer.id,
        type: "image",
        label: layer.label,
        visible: layer.visible !== false,
        locked: Boolean(layer.locked),
        zIndex: Number(layer.zIndex) || 0,
        imageUrl: url,
        x: Number(layer.x) || 0,
        y: Number(layer.y) || 0,
        width: Math.max(1, Number(layer.width) || 100),
        height: Math.max(1, Number(layer.height) || 100),
        rotation: Number(layer.rotation) || 0,
        flipH: Boolean(layer.flipH),
        flipV: Boolean(layer.flipV),
      };
    })
    .filter(Boolean);
}

function buildPersistedSlice(state) {
  return {
    creationId: state.creationId,
    transparentSourceId: state.transparentSourceId,
    rootCreationId: state.rootCreationId,
    background: sanitizeBackgroundForPersist(state.background),
    object: sanitizeObjectForPersist(state.object),
    layers: sanitizeLayersForPersist(state.layers),
  };
}

function minimalPersistedPayload(parsed) {
  const s = parsed?.state ?? {};
  return JSON.stringify({
    ...parsed,
    state: {
      creationId: s.creationId ?? null,
      transparentSourceId: s.transparentSourceId ?? null,
      rootCreationId: s.rootCreationId ?? null,
      background: { ...DEFAULT_BACKGROUND },
      object: sanitizeObjectForPersist(s.object),
      layers: createDefaultLayers(),
    },
  });
}

const safeSessionStorage = {
  getItem: (name) => {
    try {
      return sessionStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, value);
      return;
    } catch (err) {
      const isQuota =
        err?.name === "QuotaExceededError" ||
        err?.code === 22 ||
        err?.code === 1014;
      if (!isQuota) throw err;
      console.warn(
        "[editorStore] sessionStorage quota exceeded; clearing persisted editor session"
      );
      try {
        sessionStorage.removeItem(name);
      } catch {
        // ignore
      }
      try {
        sessionStorage.setItem(name, minimalPersistedPayload(JSON.parse(value)));
      } catch {
        // In-memory editor state still works; skip persist for this tab
      }
    }
  },
  removeItem: (name) => {
    try {
      sessionStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

export const useEditorStore = create(
  persist(
    (set, get) => ({
      /** Persisted: backend creation id for current canvas asset. */
      creationId: null,
      /** Persisted: bg-removed transparent source for toggle restore. */
      transparentSourceId: null,
      /** Persisted: upload-anchor creation id for the edit tree. */
      rootCreationId: null,
      /** Runtime only — never persisted (blob / fresh fetch). */
      imageUrl: null,
      /**
       * Runtime only — cached main-subject cutout while Remove BG is toggled off.
       * Kept in the store so undo/revoke logic does not destroy the blob.
       */
      subjectCutoutUrl: null,
      /** Persisted: canvas background (color / image / ai metadata). */
      background: { ...DEFAULT_BACKGROUND },
      /** Persisted: object tools (shadow, outline, rotate, flip). */
      object: {
        ...DEFAULT_OBJECT_STATE,
        shadow: { ...DEFAULT_OBJECT_STATE.shadow },
        outline: { ...DEFAULT_OBJECT_STATE.outline },
        watermark: { ...DEFAULT_OBJECT_STATE.watermark },
      },
      /** Persisted: layer visibility / order metadata (+ image overlays). */
      layers: createDefaultLayers(),
      /** Runtime only — upload library thumbnails (may use blob URLs). */
      uploadAssets: [],
      /** Runtime only — which layer row is highlighted in the Layers panel. */
      selectedLayerId: null,

      _hasHydrated: false,
      setHasHydrated: (value) => set({ _hasHydrated: value }),

      images: [],

      layout: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        alignX: "center",
        alignY: "center",
        scaleMode: "fit",
        backgroundColor: "#ffffff",
        ignorePaddingOnCrop: false,
      },

      /** Session undo stack — not persisted. */
      past: [],
      future: [],
      aiGeneration: 0,
      _historySeeded: false,

      setImages: (imagesArray) =>
        set(() => ({
          images: [...imagesArray],
        })),

      setCreationId: (creationId) =>
        set({
          creationId: creationId != null ? Number(creationId) : null,
        }),

      setLineage: ({ creationId, transparentSourceId, rootCreationId } = {}) =>
        set((state) => ({
          creationId:
            creationId !== undefined
              ? creationId != null
                ? Number(creationId)
                : null
              : state.creationId,
          transparentSourceId:
            transparentSourceId !== undefined
              ? transparentSourceId != null
                ? Number(transparentSourceId)
                : null
              : state.transparentSourceId,
          rootCreationId:
            rootCreationId !== undefined
              ? rootCreationId != null
                ? Number(rootCreationId)
                : null
              : state.rootCreationId,
        })),

      /** After bg remove or session restore — persist id + runtime blob. */
      commitImageSession: (creationId, imageUrl, lineage = {}) =>
        set((state) => {
          const shell = {
            ...state,
            creationId: creationId != null ? Number(creationId) : null,
            transparentSourceId:
              lineage.transparentSourceId != null
                ? Number(lineage.transparentSourceId)
                : creationId != null
                  ? Number(creationId)
                  : state.transparentSourceId,
            rootCreationId:
              lineage.rootCreationId != null
                ? Number(lineage.rootCreationId)
                : creationId != null
                  ? Number(creationId)
                  : state.rootCreationId,
            imageUrl: imageUrl || null,
          };
          if (state.imageUrl && state.imageUrl !== imageUrl) {
            revokeBlobIfUnreferenced(state.imageUrl, shell);
          }
          return {
            creationId: shell.creationId,
            transparentSourceId: shell.transparentSourceId,
            rootCreationId: shell.rootCreationId,
            imageUrl: imageUrl || null,
          };
        }),

      setImageUrl: (imageUrl, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const shell = { ...state, ...historyPatch, imageUrl: imageUrl || null };
          if (state.imageUrl && state.imageUrl !== imageUrl) {
            revokeBlobIfUnreferenced(state.imageUrl, shell);
          }
          return {
            ...historyPatch,
            imageUrl: imageUrl || null,
          };
        }),

      setSubjectCutoutUrl: (url) => set({ subjectCutoutUrl: url || null }),

      pushSnapshot: () =>
        set((state) => {
          if (!state._historySeeded) return {};
          return appendPast(state);
        }),

      seedHistoryBaseline: (imageUrl) =>
        set((state) => {
          if (state._historySeeded) return {};
          const nextUrl = imageUrl || state.imageUrl;
          return {
            _historySeeded: true,
            past: [],
            future: [],
            aiGeneration: 0,
            imageUrl: nextUrl,
          };
        }),

      bumpAiGeneration: () => {
        const next = get().aiGeneration + 1;
        set({ aiGeneration: next });
        return next;
      },

      applyAiResult: ({
        imageUrl,
        creationId,
        background,
        expectedGeneration,
      }) => {
        const state = get();
        if (
          expectedGeneration != null &&
          expectedGeneration !== state.aiGeneration
        ) {
          return false;
        }
        if (!state._historySeeded) return false;

        set((s) => {
          const historyPatch = appendPast(s);
          const shell = { ...s, ...historyPatch };
          const fields = applySnapshotFields(
            shell,
            {
              ...captureSnapshot(s),
              imageUrl: imageUrl ?? s.imageUrl,
              creationId:
                creationId !== undefined
                  ? creationId != null
                    ? Number(creationId)
                    : null
                  : s.creationId,
              background: background
                ? { ...s.background, ...background }
                : s.background,
            },
            s.imageUrl
          );
          return { ...historyPatch, ...fields };
        });
        return true;
      },

      /** Non-AI edit that replaces image and/or background (restore cutout, etc.). */
      applyManualEdit: ({ imageUrl, creationId, background }) => {
        const state = get();
        if (!state._historySeeded) return false;

        set((s) => {
          const historyPatch = appendPast(s);
          const shell = { ...s, ...historyPatch };
          const fields = applySnapshotFields(
            shell,
            {
              ...captureSnapshot(s),
              imageUrl: imageUrl ?? s.imageUrl,
              creationId:
                creationId !== undefined
                  ? creationId != null
                    ? Number(creationId)
                    : null
                  : s.creationId,
              background: background ?? s.background,
            },
            s.imageUrl
          );
          return { ...historyPatch, ...fields };
        });
        return true;
      },

      setBackground: (updates, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            background: {
              ...state.background,
              ...updates,
            },
          };
        }),

      resetBackground: (options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            background: { ...DEFAULT_BACKGROUND },
          };
        }),

      updateLayout: (key, value) =>
        set((state) => ({
          layout: {
            ...state.layout,
            [key]: value,
          },
        })),

      setObject: (updates, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            object: {
              ...state.object,
              ...updates,
            },
          };
        }),

      setShadow: (updates, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            object: {
              ...state.object,
              shadow: {
                ...state.object.shadow,
                ...updates,
              },
            },
          };
        }),

      setOutline: (updates, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            object: {
              ...state.object,
              outline: {
                ...state.object.outline,
                ...updates,
              },
            },
          };
        }),

      setWatermark: (updates, options = {}) =>
        set((state) => {
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            object: {
              ...state.object,
              watermark: {
                ...(state.object.watermark || DEFAULT_OBJECT_STATE.watermark),
                ...updates,
              },
            },
          };
        }),

      updateObject: (key, value, options = {}) =>
        set((state) => {
          if (Object.is(state.object?.[key], value)) return {};
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            object: {
              ...state.object,
              [key]: value,
            },
          };
        }),

      setLayerVisibility: (layerId, visible, options = {}) =>
        set((state) => {
          const layers = normalizeLayers(state.layers);
          const idx = layers.findIndex((l) => l.id === layerId);
          if (idx < 0) return {};
          if (layers[idx].visible === Boolean(visible)) return {};
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const next = layers.map((layer, i) =>
            i === idx ? { ...layer, visible: Boolean(visible) } : layer
          );
          return {
            ...historyPatch,
            layers: normalizeLayers(next),
          };
        }),

      /** Rename Object or an image overlay (classify / user rename). */
      setLayerLabel: (layerId, label, options = {}) =>
        set((state) => {
          const nextLabel =
            typeof label === "string" && label.trim() ? label.trim() : null;
          if (!layerId || !nextLabel) return {};
          const layers = normalizeLayers(state.layers);
          const idx = layers.findIndex((l) => l.id === layerId);
          if (idx < 0) return {};
          if (layers[idx].label === nextLabel) return {};
          const recordHistory = options.recordHistory === true;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const next = layers.map((layer, i) =>
            i === idx ? { ...layer, label: nextLabel } : layer
          );
          return {
            ...historyPatch,
            layers: normalizeLayers(next),
          };
        }),

      toggleLayerVisibility: (layerId, options = {}) => {
        const state = get();
        const layers = normalizeLayers(state.layers);
        const layer = layers.find((l) => l.id === layerId);
        if (!layer) return;
        get().setLayerVisibility(layerId, !layer.visible, options);
      },

      setSelectedLayerId: (layerId) =>
        set({
          selectedLayerId: layerId || null,
        }),

      selectLayer: (layerId) => {
        const next = layerId || null;
        if (get().selectedLayerId === next) return;
        set({ selectedLayerId: next });
      },

      /** Library only — does not place on canvas. Dedupes by id / rootCreationId. */
      addUploadAsset: ({ id: providedId, imageUrl, label, rootCreationId } = {}) => {
        if (!imageUrl) return null;
        const state = get();
        const existing = (state.uploadAssets || []).find(
          (a) =>
            (providedId && a.id === providedId) ||
            (rootCreationId != null &&
              String(a.rootCreationId) === String(rootCreationId))
        );
        if (existing) return existing.id;

        const id =
          providedId ||
          `asset_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
        const assetLabel =
          (typeof label === "string" && label.trim()) ||
          `Image ${(state.uploadAssets?.length || 0) + 1}`;
        set({
          uploadAssets: [
            {
              id,
              layerId: null,
              label: assetLabel,
              imageUrl,
              rootCreationId: rootCreationId ?? null,
              createdAt: Date.now(),
            },
            ...(state.uploadAssets || []),
          ],
        });
        return id;
      },

      /** Merge remote dashboard uploads into the library (newest first, no dupes). */
      mergeRemoteUploadAssets: (items = []) => {
        if (!Array.isArray(items) || items.length === 0) return;
        set((state) => {
          const isBlob = (url) =>
            typeof url === "string" && url.startsWith("blob:");
          const byId = new Map();
          const rootToId = new Map();

          const put = (asset, { allowReplaceBlob = false } = {}) => {
            if (!asset?.id) return;
            const hasVisual =
              (typeof asset.imageUrl === "string" &&
                asset.imageUrl.length > 0 &&
                !asset.imageUrl.startsWith("pending-fetch:")) ||
              asset.rootCreationId != null;
            if (!hasVisual) return;
            const rootKey =
              asset.rootCreationId != null
                ? `root:${asset.rootCreationId}`
                : null;
            const existingId = rootKey ? rootToId.get(rootKey) : null;
            const existing = byId.get(asset.id) || (existingId && byId.get(existingId));

            if (existing) {
              const existingIsBlob = isBlob(existing.imageUrl);
              const nextIsBlob = isBlob(asset.imageUrl);
              const existingIsPlaceholder =
                !existing.imageUrl ||
                String(existing.imageUrl).startsWith("pending-fetch:");
              const nextHasRealUrl =
                asset.imageUrl &&
                !String(asset.imageUrl).startsWith("pending-fetch:");
              if (
                (allowReplaceBlob && existingIsBlob && !nextIsBlob && nextHasRealUrl) ||
                (existingIsPlaceholder && nextHasRealUrl)
              ) {
                byId.delete(existing.id);
                if (existing.rootCreationId != null) {
                  rootToId.delete(`root:${existing.rootCreationId}`);
                }
              } else {
                return;
              }
            }

            byId.set(asset.id, {
              ...asset,
              imageUrl: asset.imageUrl || null,
            });
            if (rootKey) rootToId.set(rootKey, asset.id);
          };

          // Remote first — replaces any prior blob: entries for the same upload
          for (const item of items) {
            const rootId =
              item.root_creation_id ?? item.rootCreationId ?? item.creation_id;
            put(
              {
                id: item.id || (rootId != null ? `upload:${rootId}` : null),
                layerId: null,
                label:
                  (typeof item.label === "string" &&
                    item.label.trim() &&
                    item.label.trim().toLowerCase() !== "object" &&
                    item.label.trim().toLowerCase() !== "upload" &&
                    item.label.trim().toLowerCase() !== "image" &&
                    !/^upload[.\s]/i.test(item.label.trim())
                      ? item.label.trim()
                      : null) || "Object",
                imageUrl: item.imageUrl || item.output_url || item.image_url,
                rootCreationId: rootId ?? null,
                createdAt: item.created_at
                  ? new Date(item.created_at).getTime()
                  : Date.now(),
              },
              { allowReplaceBlob: true }
            );
          }

          // Keep session-only / pending tiles that aren't in the remote list
          for (const asset of state.uploadAssets || []) {
            put(asset);
          }

          // Match dashboard Uploads: newest first (API order_by created_at DESC)
          const sorted = Array.from(byId.values()).sort(
            (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
          );
          return { uploadAssets: sorted };
        });
      },

      /** Replace or insert a library asset (used after remote upload completes). */
      upsertUploadAsset: (asset, { replaceId } = {}) => {
        if (!asset?.id) return null;
        set((state) => {
          const list = [...(state.uploadAssets || [])];
          const sameRoot = (a) =>
            asset.rootCreationId != null &&
            a.rootCreationId != null &&
            String(a.rootCreationId) === String(asset.rootCreationId);

          let idx = list.findIndex(
            (a) =>
              a.id === asset.id ||
              (replaceId && a.id === replaceId) ||
              sameRoot(a)
          );

          const next = {
            layerId: null,
            label: asset.label || "Object",
            imageUrl: asset.imageUrl || null,
            rootCreationId: asset.rootCreationId ?? null,
            createdAt: asset.createdAt || Date.now(),
            ...asset,
          };

          if (idx >= 0) {
            // Keep list order — never bump to front on place / blob resolve.
            const prev = list[idx];
            list[idx] = {
              ...prev,
              ...next,
              createdAt: prev.createdAt || next.createdAt,
            };
            // Drop any duplicate slots for the same root/id
            const keptId = list[idx].id;
            const deduped = list.filter(
              (a, i) =>
                i === idx ||
                (a.id !== keptId &&
                  a.id !== asset.id &&
                  a.id !== replaceId &&
                  !sameRoot(a))
            );
            return { uploadAssets: deduped };
          }

          // Brand-new library tile — newest first
          return { uploadAssets: [next, ...list] };
        });
        return asset.id;
      },

      /**
       * Place image as a new canvas layer (above current top).
       * @param {{ imageUrl: string, label?: string, naturalWidth: number, naturalHeight: number, canvasWidth: number, canvasHeight: number, addToLibrary?: boolean }} payload
       */
      addUploadedImageLayer: (payload, options = {}) => {
        const {
          imageUrl,
          label,
          naturalWidth,
          naturalHeight,
          canvasWidth,
          canvasHeight,
          addToLibrary = true,
          rootCreationId = null,
        } = payload || {};
        if (!imageUrl || !canvasWidth || !canvasHeight) return null;

        const nw = Math.max(1, Number(naturalWidth) || 1);
        const nh = Math.max(1, Number(naturalHeight) || 1);
        const maxW = canvasWidth * 0.45;
        const maxH = canvasHeight * 0.45;
        const scale = Math.min(maxW / nw, maxH / nh, 1);
        const width = Math.max(40, Math.round(nw * scale));
        const height = Math.max(40, Math.round(nh * scale));
        const x = Math.round((canvasWidth - width) / 2);
        const y = Math.round((canvasHeight - height) / 2);
        const id = nextImageLayerId();
        const assetLabel =
          (typeof label === "string" && label.trim()) ||
          `Image ${(get().uploadAssets?.length || 0) + 1}`;

        set((state) => {
          const layers = normalizeLayers(state.layers);
          const maxZ = layers.reduce((m, l) => Math.max(m, l.zIndex ?? 0), 0);
          const layer = createImageLayer({
            id,
            imageUrl,
            label: assetLabel,
            zIndex: maxZ + 1,
            x,
            y,
            width,
            height,
            rootCreationId,
          });
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const nextAssets = addToLibrary
            ? [
                {
                  id: `asset_${id}`,
                  layerId: id,
                  label: assetLabel,
                  imageUrl,
                  rootCreationId:
                    rootCreationId != null && Number.isFinite(Number(rootCreationId))
                      ? Number(rootCreationId)
                      : null,
                  createdAt: Date.now(),
                },
                ...(state.uploadAssets || []),
              ]
            : state.uploadAssets || [];
          return {
            ...historyPatch,
            layers: normalizeLayers([...layers, layer]),
            uploadAssets: nextAssets,
            selectedLayerId: id,
          };
        });
        return id;
      },

      /** Place an existing upload-library asset again as a new overlay. */
      placeUploadAssetOnCanvas: (assetId, canvasSize, options = {}) => {
        const state = get();
        const asset = (state.uploadAssets || []).find((a) => a.id === assetId);
        if (!asset?.imageUrl || !canvasSize?.width || !canvasSize?.height) {
          return Promise.resolve(null);
        }
        const rootCreationId =
          asset.rootCreationId ??
          asset.root_creation_id ??
          null;
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const id = get().addUploadedImageLayer(
              {
                imageUrl: asset.imageUrl,
                label: asset.label,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                canvasWidth: canvasSize.width,
                canvasHeight: canvasSize.height,
                addToLibrary: false,
                rootCreationId,
              },
              options
            );
            resolve(id);
          };
          img.onerror = () => resolve(null);
          img.src = asset.imageUrl;
        });
      },

      updateImageLayer: (layerId, updates, options = {}) =>
        set((state) => {
          const layers = normalizeLayers(state.layers);
          const idx = layers.findIndex((l) => l.id === layerId && l.type === "image");
          if (idx < 0) return {};
          const current = layers[idx];
          const keys = Object.keys(updates || {});
          if (
            keys.length > 0 &&
            keys.every((key) => Object.is(current[key], updates[key]))
          ) {
            return {};
          }
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const next = layers.map((layer, i) =>
            i === idx ? { ...layer, ...updates, id: layer.id, type: "image" } : layer
          );
          return {
            ...historyPatch,
            layers: normalizeLayers(next),
          };
        }),

      removeImageLayer: (layerId, options = {}) =>
        set((state) => {
          const layers = normalizeLayers(state.layers);
          const target = layers.find((l) => l.id === layerId && l.type === "image");
          if (!target) return {};
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          const nextLayers = normalizeLayers(layers.filter((l) => l.id !== layerId));
          const shell = {
            ...state,
            ...historyPatch,
            layers: nextLayers,
            selectedLayerId:
              state.selectedLayerId === layerId ? "object" : state.selectedLayerId,
          };
          if (target.imageUrl?.startsWith("blob:")) {
            revokeBlobIfUnreferenced(target.imageUrl, shell);
          }
          return {
            ...historyPatch,
            layers: nextLayers,
            selectedLayerId: shell.selectedLayerId,
          };
        }),

      /**
       * Visual order: top-first ids for movable layers (object + images).
       * Background stays at zIndex 0.
       */
      reorderMovableLayers: (topFirstIds, options = {}) =>
        set((state) => {
          const layers = normalizeLayers(state.layers);
          const background = layers.find((l) => l.id === "background");
          const movable = layers.filter((l) => l.id !== "background");
          const byId = new Map(movable.map((l) => [l.id, l]));
          const ordered = [];
          for (const id of topFirstIds || []) {
            if (byId.has(id)) {
              ordered.push(byId.get(id));
              byId.delete(id);
            }
          }
          // Append any missing (shouldn't happen)
          for (const layer of byId.values()) ordered.push(layer);
          // topFirst → high zIndex
          const reindexed = ordered
            .slice()
            .reverse()
            .map((layer, i) => ({ ...layer, zIndex: i + 1 }));
          const recordHistory = options.recordHistory !== false;
          const historyPatch = recordHistory ? appendPast(state) : {};
          return {
            ...historyPatch,
            layers: normalizeLayers([background, ...reindexed]),
          };
        }),

      bringLayerForward: (layerId, options = {}) => {
        const layers = sortLayersTopFirst(get().layers);
        const movable = layers.filter((l) => l.id !== "background");
        const idx = movable.findIndex((l) => l.id === layerId);
        if (idx <= 0) return;
        const next = movable.map((l) => l.id);
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        get().reorderMovableLayers(next, options);
      },

      sendLayerBackward: (layerId, options = {}) => {
        const layers = sortLayersTopFirst(get().layers);
        const movable = layers.filter((l) => l.id !== "background");
        const idx = movable.findIndex((l) => l.id === layerId);
        if (idx < 0 || idx >= movable.length - 1) return;
        const next = movable.map((l) => l.id);
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        get().reorderMovableLayers(next, options);
      },

      bringLayerToFront: (layerId, options = {}) => {
        const layers = sortLayersTopFirst(get().layers);
        const movable = layers.filter((l) => l.id !== "background");
        if (!movable.some((l) => l.id === layerId)) return;
        const next = [
          layerId,
          ...movable.map((l) => l.id).filter((id) => id !== layerId),
        ];
        get().reorderMovableLayers(next, options);
      },

      sendLayerToBack: (layerId, options = {}) => {
        const layers = sortLayersTopFirst(get().layers);
        const movable = layers.filter((l) => l.id !== "background");
        if (!movable.some((l) => l.id === layerId)) return;
        const next = [
          ...movable.map((l) => l.id).filter((id) => id !== layerId),
          layerId,
        ];
        get().reorderMovableLayers(next, options);
      },

      resetObjectState: () =>
        set(() => ({
          object: {
            ...DEFAULT_OBJECT_STATE,
            shadow: { ...DEFAULT_OBJECT_STATE.shadow },
            outline: { ...DEFAULT_OBJECT_STATE.outline },
            watermark: { ...DEFAULT_OBJECT_STATE.watermark },
          },
        })),

      /** New upload from dashboard — clear persisted session + runtime image. */
      clearEditorSession: () =>
        set((state) => {
          revokeAllSessionBlobs(state);
          return {
            creationId: null,
            transparentSourceId: null,
            rootCreationId: null,
            imageUrl: null,
            subjectCutoutUrl: null,
            background: { ...DEFAULT_BACKGROUND },
            object: {
              ...DEFAULT_OBJECT_STATE,
              shadow: { ...DEFAULT_OBJECT_STATE.shadow },
              outline: { ...DEFAULT_OBJECT_STATE.outline },
              watermark: { ...DEFAULT_OBJECT_STATE.watermark },
            },
            layers: createDefaultLayers(),
            uploadAssets: [],
            selectedLayerId: null,
            past: [],
            future: [],
            aiGeneration: 0,
            _historySeeded: false,
          };
        }),

      undo: () =>
        set((state) => {
          if (state.past.length === 0) return {};
          const previous = state.past[state.past.length - 1];
          const current = captureSnapshot(state);
          const nextPast = state.past.slice(0, -1);
          const nextFuture = [current, ...state.future];
          const shell = { ...state, past: nextPast, future: nextFuture };
          const fields = applySnapshotFields(shell, previous, state.imageUrl);
          return {
            ...fields,
            past: nextPast,
            future: nextFuture,
            aiGeneration: state.aiGeneration + 1,
          };
        }),

      redo: () =>
        set((state) => {
          if (state.future.length === 0) return {};
          const next = state.future[0];
          const current = captureSnapshot(state);
          const nextPast = [...state.past, current];
          const nextFuture = state.future.slice(1);
          const shell = { ...state, past: nextPast, future: nextFuture };
          const fields = applySnapshotFields(shell, next, state.imageUrl);
          return {
            ...fields,
            past: nextPast,
            future: nextFuture,
            aiGeneration: state.aiGeneration + 1,
          };
        }),

      canUndo: () => get().past.length > 0,
      canRedo: () => get().future.length > 0,
    }),
    {
      name: SESSION_STORAGE_KEY,
      storage: createJSONStorage(() => safeSessionStorage),
      partialize: (state) => buildPersistedSlice(state),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn("[editorStore] rehydrate failed", error);
        }
        // Don't clobber an in-memory classify label if hydrate finishes late
        // (dashboard upload can label Object before persist rehydrate completes).
        useEditorStore.setState((current) => {
          const incoming = normalizeLayers(state?.layers);
          const currentLayers = normalizeLayers(current.layers);
          const curObj = currentLayers.find((l) => l.id === "object");
          const inObj = incoming.find((l) => l.id === "object");
          const currentHasCustom =
            typeof curObj?.label === "string" &&
            curObj.label.trim() &&
            curObj.label.trim().toLowerCase() !== "object";
          const incomingIsDefault =
            !inObj?.label ||
            String(inObj.label).trim().toLowerCase() === "object";
          return {
            _hasHydrated: true,
            past: [],
            future: [],
            aiGeneration: 0,
            _historySeeded: false,
            layers:
              currentHasCustom && incomingIsDefault
                ? currentLayers
                : incoming,
          };
        });
      },
    }
  )
);

/** Imperative clear for dashboard / new session (outside React). */
export const clearEditorSession = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
  useEditorStore.getState().clearEditorSession();
};

/** Read creationId from sessionStorage before Zustand rehydrate finishes. */
export function readPersistedCreationId() {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.state?.creationId;
    if (id == null) return null;
    const num = Number(id);
    return Number.isFinite(num) && num > 0 ? num : null;
  } catch {
    return null;
  }
}
